import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, Play, RefreshCw, Eye, Send } from 'lucide-react';
import { getBackendUrl } from '../../utils/api';
import storage from '../../utils/storage';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const API = `${getBackendUrl()}/api/first-play-alerts`;
const auth = () => ({ headers: { Authorization: `Bearer ${storage.getItem('token')}` } });
const MONTHS = [1, 3, 6, 9, 12, 18, 24];
const yesterday = () => {
  const d = new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/Athens' }));
  d.setDate(d.getDate() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const errMsg = (e) => e?.response?.data?.detail || e.message;

const SettingsCard = () => {
  const [s, setS] = useState(null);
  const [recipients, setRecipients] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/settings`, auth()).then(({ data }) => {
      setS(data);
      setRecipients((data.all_stations_recipients || []).join(', '));
    }).catch((e) => toast.error(errMsg(e)));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/settings`, { ...s, all_stations_recipients: recipients }, auth());
      setS(data);
      setRecipients((data.all_stations_recipients || []).join(', '));
      toast.success('Οι ρυθμίσεις αποθηκεύτηκαν');
    } catch (e) { toast.error(errMsg(e)); }
    setSaving(false);
  };

  if (!s) return null;
  return (
    <Card data-testid="first-play-settings-card">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2"><Mail className="h-5 w-5 text-orange-500" />Πρώτες Μεταδόσεις — Ρυθμίσεις</CardTitle>
        <CardDescription>
          Κάθε πρωί στις 07:00 (ώρα Ελλάδας), μετά την ολοκλήρωση των νυχτερινών εργασιών κανονικοποίησης, στέλνονται τα τραγούδια που μετέδωσε χθες ο σταθμός και δεν είχε μεταδώσει μέσα στο διάστημα που ορίζετε.
          Η ημερομηνία κυκλοφορίας δεν παίζει ρόλο. Οι παραλήπτες ανά σταθμό ορίζονται στο πάνελ επεξεργασίας του κάθε σταθμού.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="flex items-center justify-between">
          <Label htmlFor="fp-enabled">Αποστολή email ενεργή</Label>
          <Switch id="fp-enabled" data-testid="first-play-enabled-switch" checked={s.enabled} onCheckedChange={(v) => setS({ ...s, enabled: v })} />
        </div>
        <div>
          <Label>Διάστημα ελέγχου «πρώτης φοράς»</Label>
          <select data-testid="first-play-lookback-select" value={s.lookback_months}
            onChange={(e) => setS({ ...s, lookback_months: Number(e.target.value) })}
            className="mt-1 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm">
            {MONTHS.map((m) => <option key={m} value={m}>Τελευταίοι {m} {m === 1 ? 'μήνας' : 'μήνες'}</option>)}
          </select>
          <p className="text-xs text-gray-500 mt-1">Σταθμοί με ιστορικό καταγραφής μικρότερο από αυτό το διάστημα δεν στέλνουν email.</p>
        </div>
        <div className="border-t pt-4 space-y-3 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <Label htmlFor="fp-all">Συνολικό email «Όλοι οι σταθμοί»</Label>
            <Switch id="fp-all" data-testid="first-play-all-stations-switch" checked={s.all_stations_enabled} onCheckedChange={(v) => setS({ ...s, all_stations_enabled: v })} />
          </div>
          <Textarea data-testid="first-play-all-stations-recipients" value={recipients} onChange={(e) => setRecipients(e.target.value)}
            placeholder="email1@example.com, email2@example.com" rows={2} />
          <p className="text-xs text-gray-500">Μόνο οι παραλήπτες που εγκρίνει ο admin εδώ λαμβάνουν το συνολικό email.</p>
        </div>
        <Button onClick={save} disabled={saving} data-testid="first-play-settings-save" className="bg-orange-500 hover:bg-orange-600 text-white">
          {saving ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : null}Αποθήκευση
        </Button>
      </CardContent>
    </Card>
  );
};

const PreviewDialog = ({ target, date, onClose }) => {
  const [html, setHtml] = useState('');
  const [to, setTo] = useState('');
  const [sending, setSending] = useState(false);
  const query = `date=${date}${target?.station_id ? `&station_id=${target.station_id}` : ''}`;

  useEffect(() => {
    if (!target) return;
    setHtml('');
    axios.get(`${API}/preview?${query}`, auth()).then(({ data }) => setHtml(data)).catch((e) => toast.error(errMsg(e)));
  }, [target, query]);

  const sendTest = async () => {
    setSending(true);
    try {
      await axios.post(`${API}/test-email`, { to, station_id: target.station_id || null, date }, auth());
      toast.success(`Στάλθηκε δοκιμαστικό στο ${to}`);
    } catch (e) { toast.error(errMsg(e)); }
    setSending(false);
  };

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="first-play-preview-dialog">
        <DialogHeader><DialogTitle>Προεπισκόπηση email — {target?.label}</DialogTitle></DialogHeader>
        <iframe title="email-preview" srcDoc={html} className="w-full h-[60vh] rounded border bg-white" data-testid="first-play-preview-iframe" />
        <div className="flex gap-2">
          <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="Δοκιμαστική αποστολή σε…" data-testid="first-play-test-email-input" />
          <Button onClick={sendTest} disabled={!to || sending} data-testid="first-play-test-email-send">
            {sending ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const fmtDate = (d) => d.toLocaleDateString('el-GR', { timeZone: 'Europe/Athens' });
const eligibleFrom = (r) => {
  const d = new Date(r.history_start);
  d.setMonth(d.getMonth() + (r.lookback_months || 0));
  return fmtDate(d);
};

const HistoryCell = ({ r }) => (
  <td className="py-2 pr-2" data-testid={`first-play-history-${r.station_id}`}>
    {r.eligible ? <Badge className="bg-green-600">Επαρκές</Badge> : <Badge variant="outline">Ανεπαρκές</Badge>}
    <div className="text-[11px] text-gray-500 mt-1">
      από {fmtDate(new Date(r.history_start))}{!r.eligible && ` · επαρκές από ${eligibleFrom(r)}`}
    </div>
  </td>
);

const StationRow = ({ r, onPreview }) => (
  <tr className="border-b dark:border-gray-700" data-testid={`first-play-row-${r.station_id}`}>
    <td className="py-2 pr-2 font-medium">{r.station_name}</td>
    <td className="py-2 pr-2 text-center font-semibold" data-testid={`first-play-count-${r.station_id}`}>{r.new_count}</td>
    <HistoryCell r={r} />
    <td className="py-2 pr-2 text-xs">{r.alert_enabled ? (r.alert_emails.join(', ') || '—') : 'Ανενεργό'}</td>
    <td className="py-2 pr-2 text-xs">{r.email_sent_at ? new Date(r.email_sent_at).toLocaleString('el-GR') : '—'}</td>
    <td className="py-2 text-right">
      <Button size="sm" variant="ghost" disabled={!r.new_count} onClick={() => onPreview({ station_id: r.station_id, label: r.station_name })}
        data-testid={`first-play-preview-${r.station_id}`}><Eye className="h-4 w-4" /></Button>
    </td>
  </tr>
);

const DailyCard = () => {
  const [date, setDate] = useState(yesterday());
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState(null);
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    try {
      const [d, st] = await Promise.all([axios.get(`${API}/daily?date=${date}`, auth()), axios.get(`${API}/status`, auth())]);
      setRows(d.data.stations);
      setStatus(st.data);
    } catch (e) { toast.error(errMsg(e)); }
  }, [date]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!status?.running) return undefined;
    const t = setTimeout(load, 5000);
    return () => clearTimeout(t);
  }, [status, load]);

  const run = async () => {
    try {
      const { data } = await axios.post(`${API}/run?date=${date}&send=false`, null, auth());
      toast.info(data.status === 'accepted' ? 'Ο υπολογισμός ξεκίνησε (χωρίς αποστολή email)' : 'Τρέχει ήδη υπολογισμός');
      setTimeout(load, 4000);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const last = status?.recent_runs?.[0];
  return (
    <Card data-testid="first-play-daily-card">
      <CardHeader>
        <CardTitle className="text-lg">Αποτελέσματα ημέρας</CardTitle>
        <CardDescription>
          {status?.running
            ? `Υπολογισμός σε εξέλιξη… ${last?.current ? `σταθμός ${last.current}/${last.total} (${last.current_station || ''})` : ''} — η πρώτη εκτέλεση φτιάχνει μία φορά το ιστορικό κάθε σταθμού, γι' αυτό οι σταθμοί εμφανίζονται σταδιακά.`
            : last ? `Τελευταίος υπολογισμός: ${last.date} (${last.status}, ${last.processed ?? 0} σταθμοί, ${last.errors ?? 0} σφάλματα, ${last.emails_sent ?? 0} email)` : 'Δεν έχει γίνει ακόμα υπολογισμός'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-2 items-center">
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-44" data-testid="first-play-date-input" />
          <Button variant="outline" onClick={load} data-testid="first-play-refresh"><RefreshCw className="h-4 w-4 mr-1" />Ανανέωση</Button>
          <Button variant="outline" onClick={run} data-testid="first-play-run"><Play className="h-4 w-4 mr-1" />Υπολογισμός (χωρίς email)</Button>
          <Button variant="outline" onClick={() => setPreview({ label: 'Όλοι οι σταθμοί' })} disabled={!rows.length} data-testid="first-play-preview-all">
            <Eye className="h-4 w-4 mr-1" />Όλοι οι σταθμοί
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="first-play-daily-table">
            <thead><tr className="text-left text-xs text-gray-500 border-b dark:border-gray-700">
              <th className="py-2">Σταθμός</th><th className="py-2 text-center">Πρώτες</th><th className="py-2">Ιστορικό</th>
              <th className="py-2">Παραλήπτες</th><th className="py-2">Στάλθηκε</th><th />
            </tr></thead>
            <tbody>{rows.map((r) => <StationRow key={r._id} r={r} onPreview={setPreview} />)}</tbody>
          </table>
          {!rows.length && <p className="text-sm text-gray-500 py-4" data-testid="first-play-empty">Δεν υπάρχουν αποτελέσματα για αυτή την ημέρα.</p>}
        </div>
      </CardContent>
      <PreviewDialog target={preview} date={date} onClose={() => setPreview(null)} />
    </Card>
  );
};

export default function FirstPlayAlertsPanel() {
  return (
    <div className="space-y-4" data-testid="first-play-alerts-panel">
      <SettingsCard />
      <DailyCard />
    </div>
  );
}
